/**
 * 0128 · A TRAVA DO LANÇAMENTO DE ABASTECIMENTO, do lado da tela.
 *
 * Pedido do dono (27/10/2026): o PAPEL pode travar o tipo (interno, externo)
 * e o tanque — configurado em Papéis, por quem administra. Quem garante é o
 * banco (gatilho `a_trava_do_papel`) e a rota (403 com a frase). A tela só
 * deixa de OFERECER o que eles recusariam: o tipo que sobrou vem fixo, o
 * tanque que sobrou vem fixo (o campo de opção única, `lib/escolha-unica.ts`)
 * e, sem o externo, o posto e o lubrificante saem do formulário de lançamento.
 *
 * A pergunta mora AQUI, e não em cada tela: são quatro formulários (o guiado e
 * o completo do celular, a linha rápida e o completo da mesa) respondendo "esta
 * pessoa lança do posto?". Com a regra copiada em cada um, a primeira mudança
 * deixaria um deles oferecendo o que o servidor recusa.
 *
 * A fonte é a sessão (`/sessao/atual`, `travaDoLancamento`), que lê a MESMA
 * função do banco que o gatilho lê. Sem trava, tudo é como antes.
 */
import type { TravaDoLancamento } from '@/modulos/sessao/api';

export type TipoDeAbastecimento = 'interno' | 'externo';

/** O que estas funções leem da sessão — as duas formas dela (a consulta e a memória) servem. */
export interface SessaoComTrava {
  readonly travaDoLancamento?: TravaDoLancamento | null;
}

/** A ajuda do tanque que sobrou sozinho pela trava (p.23: frase com ponto). */
/** A ajuda do posto fixo (0132): o posto parceiro lança só no posto dele. */
export const AJUDA_DO_POSTO_UNICO = 'Único posto liberado para você.';
/**
 * O posto parceiro SEM posto (31/10/2026): o fornecedor dele ainda nao foi
 * ligado a nenhum posto, e a lista vinha vazia sem dizer por que.
 */
export const AJUDA_DO_PARCEIRO_SEM_POSTO =
  'Nenhum posto está ligado ao seu fornecedor. Peça a quem administra a empresa para ligar o posto ao fornecedor em Combustível, Cadastros, Postos.';

export const AJUDA_DO_TANQUE_UNICO = 'Único tanque liberado para o seu papel.';
/** A ajuda do tipo que sobrou sozinho pela trava. */
export const AJUDA_DO_TIPO_UNICO = 'Único tipo liberado para o seu papel.';

const TODOS_OS_TIPOS: readonly TipoDeAbastecimento[] = ['interno', 'externo'];

/** A trava, ou `null` quando nada está travado (ou a sessão ainda não chegou). */
function travaDaSessao(sessao: SessaoComTrava | null | undefined): TravaDoLancamento | null {
  return sessao?.travaDoLancamento ?? null;
}

/** Os tipos que a pessoa lança: os dois, sem trava de tipo. */
export function tiposPermitidos(sessao: SessaoComTrava | null | undefined): readonly TipoDeAbastecimento[] {
  return travaDaSessao(sessao)?.tipos ?? TODOS_OS_TIPOS;
}

export function permiteTipo(sessao: SessaoComTrava | null | undefined, tipo: TipoDeAbastecimento): boolean {
  return tiposPermitidos(sessao).includes(tipo);
}

/** O tanque está travado? (Sem trava de tanque, a lista é a do cadastro.) */
export function tanqueTravado(sessao: SessaoComTrava | null | undefined): boolean {
  return (travaDaSessao(sessao)?.tanques ?? null) !== null;
}

/**
 * Os tanques que a tela pode oferecer: com trava de tanque, só os liberados;
 * sem ela, todos.
 *
 * O servidor devolve TODOS os tanques — a trava é do lançamento, não da
 * leitura. É por isso que o filtro mora aqui, e é ele que faz o tanque único
 * virar campo fixo.
 */
export function tanquesPermitidos<T extends { readonly id: string }>(
  tanques: readonly T[],
  sessao: SessaoComTrava | null | undefined,
): readonly T[] {
  const liberados = travaDaSessao(sessao)?.tanques ?? null;
  if (liberados === null) return tanques;
  const ids = new Set(liberados.map((t) => t.id));
  return tanques.filter((t) => ids.has(t.id));
}
